import React, { useEffect, useReducer, useRef } from 'react';
import API from 'helpers/API';
import Spinner from 'components/Spinner';
import styles from './systemEventTimelineStyles.scss';
import ShipmentEventRow from './ShipmentEventRow';
import { filterValidEvents } from './ShipmentEventUtils';
import updateTimelineLine from './updateTimelineLine';

const dataCache = new Map();

export const clearTimelineCache = () => dataCache.clear();

const actionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_DATA: 'SET_DATA',
  TOGGLE_DROPDOWN: 'TOGGLE_DROPDOWN',
  INCREMENT_LINE_VERSION: 'INCREMENT_LINE_VERSION',
};

const timelineReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_LOADING:
      return { ...state, loading: action.loading };
    case actionTypes.SET_ERROR:
      return { ...state, error: action.error, loading: false };
    case actionTypes.SET_DATA:
      return {
        ...state,
        events: action.events,
        timelineWarning: action.timelineWarning,
        loading: false,
        error: null,
      };
    case actionTypes.TOGGLE_DROPDOWN:
      return {
        ...state,
        openDropdowns: {
          ...state.openDropdowns,
          [action.idx]: !state.openDropdowns[action.idx],
        },
      };
    case actionTypes.INCREMENT_LINE_VERSION:
      return { ...state, lineVersion: state.lineVersion + 1 };
    default:
      return state;
  }
};

const buildInitialState = (cacheKey) => {
  if (!cacheKey) {
    return {
      events: null, timelineWarning: null, loading: false, error: null, openDropdowns: {}, lineVersion: 0,
    };
  }
  const cached = dataCache.get(cacheKey);
  return {
    events: cached ? cached.events : null,
    timelineWarning: cached ? cached.timelineWarning : null,
    loading: !cached,
    error: null,
    openDropdowns: {},
    lineVersion: 0,
  };
};

const SystemEventTimeline = ({ shipmentData }) => {
  const shipmentId = shipmentData?.id;
  const cacheKey = shipmentId ?? null;

  const [state, baseDispatch] = useReducer(
    timelineReducer,
    cacheKey,
    buildInitialState,
  );
  const dispatch = /** @type {import('react').Dispatch<any>} */ (baseDispatch);
  const timelineRef = useRef(null);
  const eventsRef = useRef(state.events);
  eventsRef.current = state.events;

  useEffect(() => {
    if (!cacheKey || dataCache.has(cacheKey)) return undefined;

    let isMounted = true;

    API.getSystemEventTimeline(shipmentId)
      .then(response => {
        if (!isMounted) {
          return;
        }

        const events = response?.shipment_events || [];
        const timelineWarning = response?.error_message || null;

        dataCache.set(cacheKey, { events, timelineWarning });
        dispatch({ type: actionTypes.SET_DATA, events, timelineWarning });
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }
        dispatch({ type: actionTypes.SET_ERROR, error: 'Failed to load shipment events.' });
      });

    return () => {
      isMounted = false;
    };
  }, [cacheKey, shipmentId, dispatch]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      if (!timelineRef.current) return;
      const events = eventsRef.current;
      if (events && events.length > 1) {
        updateTimelineLine(timelineRef.current, styles.eventRow, styles.dot);
      } else {
        timelineRef.current.style.setProperty('--timeline-height', '0px');
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [state.events, state.openDropdowns, state.lineVersion]);

  useEffect(() => {
    if (!timelineRef.current || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        if (!timelineRef.current) return;
        if (eventsRef.current && eventsRef.current.length > 1) {
          updateTimelineLine(timelineRef.current, styles.eventRow, styles.dot);
        }
      });
    });

    observer.observe(timelineRef.current);
    return () => observer.disconnect();
  }, []);

  if (state.loading) return <Spinner />;
  if (state.error) return <div className={styles.noEvents}>{state.error}</div>;

  const filteredEvents = filterValidEvents(state.events);
  if (!filteredEvents || filteredEvents.length === 0) {
    return (
      <div className={styles.noEvents}>
        There are no system events for this shipment yet.
      </div>
    );
  }

  const handleToggleDropdown = idx => {
    dispatch({ type: actionTypes.TOGGLE_DROPDOWN, idx });
  };

  const handleSectionToggle = () => {
    dispatch({ type: actionTypes.INCREMENT_LINE_VERSION });
  };

  return (
    <div className={styles.timelineContainer}>
      {state.timelineWarning && (
        <div className={styles.timelineWarning}>{state.timelineWarning}</div>
      )}
      <ol ref={timelineRef} className={styles.shipmentEventTimeline}>
        {filteredEvents.map((event, idx) => (
          <ShipmentEventRow
            key={event.id || idx}
            event={event}
            idx={idx}
            isOpen={state.openDropdowns[idx]}
            onToggle={handleToggleDropdown}
            onSectionToggle={handleSectionToggle}
          />
        ))}
      </ol>
    </div>
  );
};

export default SystemEventTimeline;
